// @givmo/auth — Firebase app + Auth initialization (env-injected config; NO hardcoded credentials).
//
// The package provides the auth LAYER. The real Firebase config (apiKey, authDomain, projectId, …)
// and the App Check ReCaptcha Enterprise site key are supplied by the CONSUMING app at runtime —
// per the Givmo per-env convention (Flutter used `--dart-define` flavors selecting `AppConfig`; the
// React apps inject equivalents from their own env). This module never embeds a project's secrets.
//
// Idempotent by design: `getApps()`/`getApp()` mean a double-init (HMR, SSR re-entry, multiple
// importers) reuses the existing app rather than throwing `app/duplicate-app`.

import {
  getApp,
  getApps,
  initializeApp,
  type FirebaseApp,
  type FirebaseOptions,
} from 'firebase/app';
import { getAuth, type Auth } from 'firebase/auth';

/**
 * The Firebase web config a consuming app injects. This is the standard `FirebaseOptions` shape
 * (apiKey/authDomain/projectId/appId/…); none of it is secret in the web-client sense, but it is
 * per-environment and therefore env-injected, never baked into this package.
 */
export type GivmoFirebaseOptions = FirebaseOptions;

/**
 * Initialize (or reuse) the Givmo Firebase app. Pass a `name` to run a NAMED secondary app — the
 * SSR donate/consumer apps may want an isolated instance per request so server-side state never
 * leaks across requests; client apps use the default (unnamed) app.
 */
export function initFirebaseApp(options: GivmoFirebaseOptions, name?: string): FirebaseApp {
  if (name !== undefined) {
    const existing = getApps().find((app) => app.name === name);
    return existing ?? initializeApp(options, name);
  }
  return getApps().length > 0 ? getApp() : initializeApp(options);
}

/** Get the `Auth` instance for an app. Thin re-export so consumers import one symbol from us. */
export function getFirebaseAuth(app: FirebaseApp): Auth {
  return getAuth(app);
}
